/* eslint-disable react/jsx-key */
/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useContext, useState, useEffect, useRef } from 'react'
import { strCol, intCol, dateTimeCol } from '@integec/grid-tools/lib/cols'
import * as Actions from 'core/EventActions'
import {
  modifierCol,
  modifiedDateCol,
  utilityReqHeader,
  unitHeader,
} from 'core/HeaderColTypeProps'
import { getApiTargetInfo, UnitSelection, ExportComponent } from 'core'
import { connect } from 'react-redux'
import R from 'ramda'
import {
  getUtilityNameMap,
  getUnitNameMap,
  getUnits,
  getPermitActions,
  getEnvConfig,
} from 'core/selectors'
import { Button, Input, message, Select, Tabs } from 'antd'
import { UnitSelectionContext, SpinContext, ErrorContext } from 'context'
import {
  CriteriaRow,
  Title,
  Content,
  FullHeightSizer,
  Filler,
  Label,
} from 'core/screen-util'
import Grid, { virtualizedGridRenderer } from '@integec/grid-tools'
import {
  rowHeaderRender,
  hasAccesPermits,
  exportDataComponent,
} from 'core/utils'
import { card97Exclusion, saveCard97Exclusion } from 'api/pgads'
import * as freeEditEngine from '@integec/grid-tools/lib/editEngine'
const curYear = new Date().getFullYear()
const yearOption = R.range(curYear - 18, curYear + 4)
  .sort((a, b) => b - a)
  .map(i => <Select.Option value={i}>{i}</Select.Option>)

export const headers = [
  unitHeader,
  intCol({ ident: 'year', display: 'Year', isKey: true, width: 50 }),
  intCol({
    ident: 'eventnum',
    display: 'Event Number',
    isKey: true,
    width: 60,
  }),
  intCol({ ident: 'eventIdx', display: 'Event Index', isKey: true, width: 60 }),
  strCol({
    ident: 'eventType',
    display: 'Event Type',
    isKey: true,
    isFiltered: true,
    width: 60,
  }),
  utilityReqHeader,
  dateTimeCol({
    ident: 'eventStart',
    display: 'Event Start Date',
    width: 150,
    displayFormat: 'MM/DD/YYYY HH:mm',
    isKey: true,
  }),
  dateTimeCol({
    ident: 'eventEnd',
    display: 'Event End Date',
    width: 150,
    displayFormat: 'MM/DD/YYYY HH:mm',
    isKey: true,
  }),
  strCol({ ident: 'availCap', display: 'Available Capacity', isKey: true }),
  intCol({
    ident: 'causeCode',
    display: 'Cause Code',
    isFiltered: true,
    isKey: true,
  }),
  strCol({
    ident: 'note',
    display: 'Note',
    width: 500,
    alignment: 'left',
    isEditable: true,
  }),
  modifierCol,
  modifiedDateCol,
  // strCol({ ident: 'lock', display: 'Lock' }),
].map(e => ({ ...e, isFiltered: true }))

const Card97Exclusion = ({
  utilityNameMap,
  unitNameMap,
  // utilityUnitMap,
  apiTargetInfo,
  actionPermits,
  envConfig,
}) => {
  const { authGodMode } = envConfig
  const hasWritePermission = hasAccesPermits({
    authGodMode,
    actionPermits,
    eventActions: [Actions.CARD97_EXCLUSION_EVENT_WRITE],
    matchAny: true,
  })
  const { setSpinning } = useContext(SpinContext)
  const { setError } = useContext(ErrorContext)
  const [data, setData] = useState([])
  const [noNotedata, setNoNoteData] = useState([])
  const [activeKey, setActiveKey] = useState('data')
  const { selectedUnits, setVisibleUnitSelection } = useContext(
    UnitSelectionContext
  )
  const [year, setYear] = useState(curYear)

  const [editInfo, setEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )
  const [noNoteEditInfo, setNoNoteEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )

  const clearDAta = _ => {
    setData([])
    setNoNoteData([])
    setEditInfo(freeEditEngine.generateInitialEditInfo())
    setNoNoteEditInfo(freeEditEngine.generateInitialEditInfo())
  }
  useEffect(clearDAta, [selectedUnits, year])

  const onRetrieveData = ({ selUnits = selectedUnits }) => {
    if (selUnits.length <= 0) {
      setVisibleUnitSelection(true)
      message.error('Please,Select Unit')
      return
    }

    setSpinning(true)
    card97Exclusion(apiTargetInfo)(
      year || curYear,
      selUnits.map(u => u.unitId)
    ).fork(setError, data => {
      setSpinning(false)
      setActiveKey('data')
      data.filter(f => f.note && f.note.length > 0)
      setData(data.filter(f => f.note && f.note.length > 0))
      setNoNoteData(data.filter(f => !f.note || f.note.length <= 0))
    })
  }
  const [fuzzyFilter, setFuzzyFilter] = useState('')

  const headerWithFormatter = headers.map(header =>
    header.ident === 'utility'
      ? {
          ...header,
          // ...toSelectionColProps(utilityNameMap),
          dataFormatter: ({ value }) => utilityNameMap[value] || value,
          sortIndexGetter: ({ rowData }) =>
            utilityNameMap[rowData.utility] || rowData.utility,
        }
      : header.ident === 'unit'
      ? {
          ...header,
          // choices: [],
          dataFormatter: ({ value }) => unitNameMap[value] || value,
          sortIndexGetter: ({ rowData }) =>
            unitNameMap[rowData.unit] || rowData.unit,
        }
      : header
  )

  const editRow = editInfo => (data, setEditInfo, setData) => {
    const modData = data.map(d => {
      if (editInfo.updatedMap.get(d)) {
        return {
          ...editInfo.updatedMap.get(d),
          modificationInfo: undefined,
          updated: true,
        }
      } else {
        return d
      }
    })

    let editData = editInfo.updated
    editData = editData.map(d => ({
      ...d,
      modificationInfo: undefined,
    }))
    setEditInfo({ ...editInfo, updated: editData })
    setData(modData)
  }

  const isEditable = ({ header }) => {
    if (!hasWritePermission) return false
    if (header.ident === 'modifier' || header.ident === 'modifiedDate')
      return false

    return !header.isKey || header.isEditable || false
  }

  const onSaveData = () => {
    let saveData = freeEditEngine.applyEdits({
      data: data.filter(d => d.updated),
      editInfo,
    })

    saveData = saveData.map(d => ({
      ...d,
      unit: d.unit.substr(d.utility.length),
    }))

    let noNoteSaveData = freeEditEngine.applyEdits({
      data: noNotedata.filter(d => d.updated),
    })

    noNoteSaveData = noNoteSaveData.map(d => ({
      ...d,
      unit: d.unit.substr(d.utility.length),
    }))

    saveData = saveData.concat(noNoteSaveData)
    if (saveData.length <= 0) {
      message.info('There is no data to Save , Please Update data for Save')
      return
    }

    setSpinning(true)
    saveCard97Exclusion(apiTargetInfo)(saveData).fork(setError, _ => {
      setSpinning(false)

      // this.setState({ editInfo: freeEditEngine.generateInitialEditInfo() })
      setEditInfo(freeEditEngine.generateInitialEditInfo())
      setNoNoteEditInfo(freeEditEngine.generateInitialEditInfo())
      onRetrieveData({})
    })
  }
  const GridData = useRef()
  const GridRef = useRef()
  const exportData = _ => {
    const avaDataExport = exportDataComponent(GridData, data || [], fuzzyFilter)
    const remDataExport = exportDataComponent(
      GridRef,
      noNotedata || [],
      fuzzyFilter
    )
    const actData = [...avaDataExport, ...remDataExport]
    new ExportComponent({
      headers: headerWithFormatter,
      data: avaDataExport,
      fileName: 'Card97 - Excluded Event Data',
      // getData: _ => avaDataExport,
      getData: _ => actData,
    }).exportCSVData()
  }

  return (
    <div
      style={{
        backgroundColor: '#f0f2f5',
        marginLeft: '20px',
        marginRight: '20px',
      }}
    >
      <UnitSelection
        setData={clearDAta}
        accesskey="u"
        // retrieveData={onRetrieveData}
        // renderContent={renderUnitContent}
        retrieveData={onRetrieveData}
      />
      <CriteriaRow style={{}}>
        <Title
          style={{
            marginRight: '15px',
            marginBottom: '5px',
            marginLeft: '10px',
            minWidth: '200px',
          }}
        >
          Card97 - Excluded Event Data
        </Title>
        <Label css={{ marginLeft: '50px', width: '50px' }}>Year : </Label>

        <Select
          style={{ minWidth: '90px', marginLeft: '20px' }}
          value={year}
          onChange={year => {
            setYear(year)
            setData([])
          }}
        >
          {yearOption}
        </Select>
        <Button
          style={{ marginLeft: '20px' }}
          onClick={onRetrieveData}
          type="primary"
          icon="database"
        >
          Retrieve
        </Button>
        {hasWritePermission && (
          <Button
            style={{ marginLeft: '20px' }}
            onClick={onSaveData}
            icon="edit"
            type="primary"
          >
            Save
          </Button>
        )}
        <Filler />
        <Input.Search
          placeholder="Filter"
          value={fuzzyFilter}
          style={{ marginLeft: '10px', marginRight: '10px', maxWidth: '200px' }}
          onChange={e => setFuzzyFilter(e.target.value)}
        />
        <Button icon="export" onClick={exportData}>
          Export
        </Button>
      </CriteriaRow>
      <FullHeightSizer heightOffset={300}>
        {({ height, width }) => (
          <Content style={{ height: height - 20 }}>
            <Tabs
              style={{ height: height - 20 }}
              type="card"
              activeKey={activeKey}
              onChange={setActiveKey}
            >
              <Tabs.TabPane tab="Data" key="data">
                <Grid
                  ref={GridData}
                  sortEnabled
                  data={data}
                  headers={headerWithFormatter}
                  altBy={u => u.unit}
                  altBgColor="#ebebeb"
                  // onEditInfoChange={info => editRow(info)}
                  // editInfo={editInfo}
                  render={virtualizedGridRenderer({
                    autoFixColByKey: false,
                    rowHeaderRender,
                    height: height - 120,
                    width: width - 50,
                  })}
                  fuzzyFilter={fuzzyFilter}
                  isEditable={isEditable}
                  selectionMode="single"
                  selectionType="row"
                  initialSortOptions={[
                    utilityReqHeader,
                    unitHeader,
                    { ident: 'year', sortOrder: 'asc' },
                    { ident: 'eventStart', sortOrder: 'asc' },
                    { ident: 'eventEnd', sortOrder: 'asc' },
                    // { ident: 'year', sortOrder: 'asc' },
                    { ident: 'eventnum', sortOrder: 'asc' },
                    // { ident: 'eventIdx', sortOrder: 'asc' },
                  ]}
                  onEditInfoChange={editInfo =>
                    editRow(editInfo)(data, setEditInfo, setData)
                  }
                  editInfo={editInfo}
                  editMode="cell"
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab="Remaining" key="remaining">
                <Grid
                  ref={GridRef}
                  sortEnabled
                  data={noNotedata}
                  headers={headerWithFormatter}
                  altBy={u => u.unit}
                  altBgColor="#ebebeb"
                  // onEditInfoChange={info => editRow(info)}
                  // editInfo={editInfo}
                  render={virtualizedGridRenderer({
                    autoFixColByKey: false,
                    rowHeaderRender,
                    height: height - 120,
                    width: width - 50,
                  })}
                  fuzzyFilter={fuzzyFilter}
                  isEditable={isEditable}
                  selectionMode="single"
                  selectionType="row"
                  initialSortOptions={[
                    utilityReqHeader,
                    unitHeader,
                    { ident: 'year', sortOrder: 'asc' },
                    { ident: 'eventStart', sortOrder: 'asc' },
                    { ident: 'eventEnd', sortOrder: 'asc' },
                    // { ident: 'year', sortOrder: 'asc' },
                    { ident: 'eventnum', sortOrder: 'asc' },
                    // { ident: 'eventIdx', sortOrder: 'asc' },
                  ]}
                  onEditInfoChange={editInfo =>
                    editRow(editInfo)(
                      noNotedata,
                      setNoNoteEditInfo,
                      setNoNoteData
                    )
                  }
                  editInfo={noNoteEditInfo}
                  editMode="cell"
                />
              </Tabs.TabPane>
            </Tabs>
          </Content>
        )}
      </FullHeightSizer>
    </div>
  )
}
const mapStateToProps = state => ({
  apiTargetInfo: getApiTargetInfo(state),
  utilityNameMap: getUtilityNameMap(state),
  unitNameMap: getUnitNameMap(state),
  actionPermits: getPermitActions(state),
  envConfig: getEnvConfig(state),
  units: getUnits(state),
})
export default connect(mapStateToProps)(Card97Exclusion)
